import React, { useState, useRef } from "react";
import { View, TextInput, StyleSheet, Pressable, Platform } from "react-native";
import { SubmitIcon } from "../../components/svgIcons";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../hooks/useResponsiveScreen";

interface ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput = ({
  value,
  onChangeText,
  onSubmit,
  disabled = false,
  placeholder = "Type a message...",
}: ChatInputProps) => {
  const { isMedium } = useResponsiveScreen();
  const inputRef = useRef<TextInput>(null);
  const INITIAL_HEIGHT = isMedium ? 48 : 36;
  const [inputHeight, setInputHeight] = useState(INITIAL_HEIGHT);

  const handleContentSizeChange = (event: any) => {
    const newHeight = event.nativeEvent.contentSize.height;
    if (newHeight > 120) {
      return;
    }
    setInputHeight(newHeight > INITIAL_HEIGHT ? newHeight : INITIAL_HEIGHT);
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSubmit();
      }
    }
  };

  const handleChangeText = (text: string) => {
    if (text.length === 0) {
      setInputHeight(INITIAL_HEIGHT);
    }
    onChangeText(text);
  };

  const isAtInitialHeight = inputHeight === INITIAL_HEIGHT;

  return (
    <View style={styles.container}>
      <View style={[styles.inputContainer, { height: Math.min(inputHeight, 120) }]}>
        <TextInput
          ref={inputRef}
          value={value}
          maxLength={7500}
          multiline
          placeholder={placeholder}
          placeholderTextColor="#808080"
          style={[
            styles.textInput,
            isMedium ? styles.textInputMedium : styles.textInputMobile,
            { height: inputHeight },
            isAtInitialHeight && styles.textInputCentered,
          ]}
          textAlignVertical={isAtInitialHeight ? "center" : "top"}
          onChangeText={handleChangeText}
          onSubmitEditing={onSubmit}
          onKeyPress={handleKeyPress}
          onContentSizeChange={handleContentSizeChange}
          scrollEnabled={true}
          editable={!disabled}
          aria-label="Chat text input"
        />
        <View style={styles.buttonContainer}>
          <CustomButton
            text=""
            rightIcon={null}
            textStyle={{}}
            disabled={disabled || !value.trim()}
            leftIcon={<SubmitIcon />}
            styleType="transparent"
            style={styles.button}
            onPress={onSubmit}
            textProps={{}}
            aria-label="Submit message"
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderTopColor: "#EAECF0",
    borderTopWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F4F7",
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
  },
  textInput: {
    flex: 1,
    fontWeight: "400",
    fontFamily: "Inter",
    color: "#101828",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    maxHeight: 120,
  },
  textInputCentered: {
    textAlignVertical: "center",
  },
  textInputMedium: {
    fontSize: 16,
  },
  textInputMobile: {
    fontSize: 14,
  },
  buttonContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    padding: 0,
  },
});

export default ChatInput;
